/**
 * T-010 — Stage 4 execution engine + pipeline orchestrator (the apply).
 *
 * `applyViaHybridEngine` is the single entry point. It composes the whole pipeline and executes the
 * resolved ops against Word as native tracked changes:
 *
 *   read live baseline → extract protected regions (T-007) → buildApplyPlan (T-006/03/04/05/08)
 *     → pre-flight locate (T-009) → execute right-to-left → restore state
 *
 * Key properties:
 *  - **Atomic.** Execution runs only if pre-flight resolves every op; otherwise it returns
 *    `applied:false` having mutated nothing (a clean no-op, not the old partial corruption).
 *  - **Native revisions.** Track changes is forced on and edits use the native Range API
 *    (`delete` / `insertText`), not `insertOoxml` — so Word records real revisions, nests a deletion
 *    of counterparty-inserted text inside their insertion, and keeps fields/bookmarks physically
 *    intact.
 *  - **Right-to-left.** Ops apply by descending baseline offset (pre-flight already ordered them), so
 *    editing one range never shifts the document position of a range not yet edited.
 *  - **Batched syncs.** All mutations are queued and flushed together — not one sync per op.
 *
 * The pure composition is `buildApplyPlan` (unit-tested). This module is the Word-facing shell,
 * validated by hand in real Word (Stage 4 is manual by design).
 */

import { buildApplyPlan } from './buildApplyPlan';
import { createProtectedRegionSet } from './protectedRegions';
import { extractProtectedRegions } from './extractProtectedRegions';
import { extractRevisionsForNormalizedBaseline } from './extractRevisions';
import { debugHybridPipeline } from './debugPipeline';
import { preflightLocate } from './preflightLocate';
import { unsearchableCharCodesOf } from './locateOps';
import {
  acceptedExtractLooksUnusable,
  acceptedTextFromOoxmlPackage,
  acceptedTextFromVisibleAndDeletes,
  applyTargetLooksTruncated,
  foldApplyText,
  foldedApplyMatchesTarget,
  ooxmlParagraphHasProtectedMarkup,
} from './applyVerify';
import { RevisionContext } from './types';
import type { SubmitRevertContext, TrackedChangeFingerprint } from '../../word/trackChanges/RedlineInserter';
import { OoxmlPackageWrapper } from '../../word/utils/OoxmlPackageWrapper';
import { normalizeWordParagraphBaseline } from '../../utils/bidiText';
import {
  createQrProfiler,
  logQuickResponseBidiCompare,
  logQuickResponseStage,
  qrTextSnapshot,
} from '../../utils/quickResponseDebug';

/* global Word */

function controlCharStats(text: string): { commentRefs: number; bel: number; otherC0: number } {
  let commentRefs = 0;
  let bel = 0;
  let otherC0 = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 5) commentRefs += 1;
    else if (c === 7) bel += 1;
    else if (c < 32 && c !== 9 && c !== 10 && c !== 13) otherC0 += 1;
  }
  return { commentRefs, bel, otherC0 };
}

function ooxmlCommentMarkupCounts(xml: string): Record<string, number> {
  const count = (re: RegExp): number => (xml.match(re) || []).length;
  return {
    commentRangeStart: count(/commentRangeStart/gi),
    commentRangeEnd: count(/commentRangeEnd/gi),
    commentReference: count(/commentReference/gi),
    bookmarkStart: count(/bookmarkStart/gi),
    u0005: count(/\u0005/g),
  };
}

/**
 * Snapshot the paragraph's tracked-change fingerprints (date+type) for undo matching. Author-free —
 * Office.js stamps native edits with the live user, so undo relies on fingerprints + an apply-time
 * window, not authorship. Mirrors RedlineInserter.collectTrackedChangeFingerprints so the hybrid and
 * legacy undo paths use the same signal. Never throws (Win32 can reject getTrackedChanges).
 */
async function collectFingerprints(
  paragraph: Word.Paragraph,
  context: Word.RequestContext,
): Promise<TrackedChangeFingerprint[]> {
  try {
    const tcs = (paragraph as unknown as {
      getTrackedChanges: () => { items: Array<{ load: (p: string[]) => void; date?: Date | string; type?: string }> };
    }).getTrackedChanges();
    (tcs as unknown as { load: (p: string) => void }).load('items');
    await context.sync();
    const items = tcs.items || [];
    for (const tc of items) tc.load(['date', 'type']);
    await context.sync();
    return items.map((tc) => ({
      dateIso: tc.date ? new Date(tc.date).toISOString() : '',
      type: String(tc.type ?? ''),
    }));
  } catch {
    return [];
  }
}

/** Fingerprints present in `after` beyond `before` (count-aware) — the changes this apply added. */
function addedFingerprints(
  before: TrackedChangeFingerprint[],
  after: TrackedChangeFingerprint[],
): TrackedChangeFingerprint[] {
  const key = (f: TrackedChangeFingerprint) => `${f.dateIso}|${f.type}`;
  const beforeCount = new Map<string, number>();
  for (const f of before) beforeCount.set(key(f), (beforeCount.get(key(f)) ?? 0) + 1);
  const added: TrackedChangeFingerprint[] = [];
  for (const f of after) {
    const c = beforeCount.get(key(f)) ?? 0;
    if (c > 0) beforeCount.set(key(f), c - 1);
    else added.push(f);
  }
  return added;
}

/** Restore the pre-apply OOXML snapshot. Track changes must be off so this is not itself a revision. */
async function restoreParagraphOoxml(
  paragraph: Word.Paragraph,
  packageXml: string,
  baselineVisible: string,
  inTable: boolean,
  sync: () => Promise<void>,
): Promise<boolean> {
  const insertable = OoxmlPackageWrapper.toInsertableParagraphPackage(packageXml, baselineVisible);
  if (!insertable) return false;
  const locations: Array<'Whole' | 'Content'> = inTable ? ['Whole', 'Content'] : ['Whole'];
  for (const location of locations) {
    try {
      paragraph.getRange(location).insertOoxml(insertable, Word.InsertLocation.replace);
      await sync();
      return true;
    } catch {
      // try the next location
    }
  }
  return false;
}

/**
 * Accepted text of THIS paragraph. `getOoxml()` packages include leftover CR
 * cells; `getReviewedText` on this doc returned a 92-char comment fragment
 * (`\\u0005`). Strip Deleted tracked-change runs from `paragraph.text`.
 */
export async function readParagraphForVerify(
  paragraph: Word.Paragraph,
  sync: () => Promise<void>,
): Promise<{ visible: string; accepted: string }> {
  // Mac Word Office.js returns 0 items from paragraph.getTrackedChanges() even
  // when the paragraph has native track changes visible. That falsely equated
  // visible (245 + inserts) with accepted, forcing verify_mismatch after any
  // insert+delete plan. Read the paragraph's OOXML directly and compute accepted
  // by skipping <w:del> and including <w:ins> runs. Fall back to the tracked-
  // changes API only if OOXML fails.
  paragraph.load('text');
  const pkgRef = (
    paragraph as unknown as { getOoxml: () => { value?: string } }
  ).getOoxml();
  const tcs = (
    paragraph as unknown as {
      getTrackedChanges: () => {
        load: (s: string) => void;
        items?: Array<{
          type?: string;
          load: (p: string | string[]) => void;
          getRange: () => { load: (s: string) => void; text?: string };
        }>;
      };
    }
  ).getTrackedChanges();
  tcs.load('items');
  await sync();
  const visible = normalizeWordParagraphBaseline(paragraph.text || '');

  const pkgXml = String(pkgRef?.value ?? '');
  const acceptedFromXml = pkgXml
    ? normalizeWordParagraphBaseline(acceptedTextFromOoxmlPackage(pkgXml, visible))
    : '';
  const xmlUsable =
    !!acceptedFromXml &&
    acceptedFromXml !== visible &&
    !acceptedExtractLooksUnusable(visible, acceptedFromXml);

  const items = tcs.items || [];
  const pending: Array<{ tc: { type?: string }; range: { text?: string } }> = [];
  for (const tc of items) {
    tc.load('type');
    const range = tc.getRange();
    range.load('text');
    pending.push({ tc, range });
  }
  if (pending.length) await sync();
  const deleted: string[] = [];
  for (const item of pending) {
    const type = String(item.tc.type ?? '').toLowerCase();
    if (type === 'deleted' || type === '2') {
      deleted.push(item.range.text || '');
    }
  }
  const acceptedFromApi = normalizeWordParagraphBaseline(
    acceptedTextFromVisibleAndDeletes(visible, deleted),
  );
  const accepted = xmlUsable ? acceptedFromXml : acceptedFromApi;
  try {
    logQuickResponseStage('readParagraphForVerify: source snapshot', {
      visibleLen: visible.length,
      acceptedFromXmlLen: acceptedFromXml.length,
      acceptedFromApiLen: acceptedFromApi.length,
      chosen: xmlUsable ? 'ooxml' : 'trackedChanges',
      pkgXmlLen: pkgXml.length,
      itemsCount: items.length,
      deletedCount: deleted.length,
      deletedTotalLen: deleted.reduce((n, s) => n + (s ? s.length : 0), 0),
      visibleControls: controlCharStats(visible),
      acceptedControls: controlCharStats(accepted),
      ooxmlMarkup: ooxmlCommentMarkupCounts(pkgXml),
    });
  } catch {}
  return { visible, accepted };
}

function buildHybridRevertContext(args: {
  paragraphIndex: number;
  applyStartedAtMs: number;
  applyFinishedAtMs: number;
  addedFingerprints: TrackedChangeFingerprint[];
  baselineVisible: string;
  rawOoxmlPackage: string;
  inTable: boolean;
}): SubmitRevertContext {
  const inTable =
    args.inTable || OoxmlPackageWrapper.containsTableMarkup(args.rawOoxmlPackage);
  const pruned = args.rawOoxmlPackage
    ? inTable
      ? OoxmlPackageWrapper.extractCellParagraphPackage(
          args.rawOoxmlPackage,
          args.baselineVisible,
        )
      : OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(
          args.rawOoxmlPackage,
          args.baselineVisible,
        )
    : '';
  return {
    applyStartedAtMs: args.applyStartedAtMs,
    applyFinishedAtMs: args.applyFinishedAtMs,
    addedFingerprints: args.addedFingerprints,
    paragraphIndex: args.paragraphIndex,
    // Restore the pre-apply OOXML snapshot (includes counterparty w:ins/w:del). Table cells
    // store a lifted <w:p> package — never the wrapping w:tbl.
    preferOriginalRestore: Boolean(pruned),
    ...(inTable ? { inTable: true } : {}),
    originalParagraphText: args.baselineVisible,
    ...(pruned ? { originalParagraphOoxmlPackage: pruned } : {}),
  };
}

async function paragraphIsInTable(
  paragraph: Word.Paragraph,
  context: Word.RequestContext,
): Promise<boolean> {
  const p = paragraph as Word.Paragraph & {
    parentTable?: { load: (s: string) => void };
    parentTableOrNullObject?: { isNullObject?: boolean; load: (s: string) => void };
  };
  try {
    if (p.parentTableOrNullObject) {
      p.parentTableOrNullObject.load('isNullObject');
      await context.sync();
      return p.parentTableOrNullObject.isNullObject === false;
    }
  } catch {
    // Older Word builds may not expose parentTableOrNullObject.
  }
  try {
    if (!p.parentTable) return false;
    p.parentTable.load('rowCount');
    await context.sync();
    return true;
  } catch {
    return false;
  }
}

export interface HybridApplyOptions {
  /** Pre-existing revisions in the paragraph (visible-baseline coords). Default: none. */
  revisionContext?: RevisionContext;
  /**
   * The target text came from a user manually editing the card (Edit → Save),
   * not an AI suggestion — skip the truncation heuristic below, which exists
   * to catch garbled/incomplete AI extractions, not to second-guess a
   * deliberate manual edit that can legitimately end mid-sentence.
   */
  skipTruncationGuard?: boolean;
}

/** Structural, text-free trace of an apply attempt (T-012 formalizes/guards this). */
export interface HybridApplyTrace {
  baselineLength: number;
  targetLength: number;
  ops: { keep: number; delete: number; insert: number };
  protectedRegions: { field: number; bookmark: number; comment?: number; total: number };
  protectedOpCount: number;
  preflight: {
    ok: boolean;
    total: number;
    resolved: number;
    failures: Array<{ kind: string; reason: string }>;
  };
  /** Number of ops skipped (field-protected / unsearchable / not found) — applied the rest. */
  skipped: number;
  /** Number of whole fields deleted because a redline fully spanned them. */
  fieldDeletes: number;
  /** Distinct char codes that made some op's search value unsearchable (diagnostics; codes only). */
  unsearchableCodes: number[];
  syncCount: number;
  outcome: 'applied' | 'applied_partial' | 'nothing_to_apply' | 'error';
}

export interface HybridApplyResult {
  applied: boolean;
  reason?: string;
  trace: HybridApplyTrace;
  /** Undo context: fingerprints + time window of the changes this apply added (null if nothing applied). */
  revertContext?: SubmitRevertContext;
}

/**
 * Apply a clean target to one paragraph via the hybrid engine, as native tracked changes.
 *
 * @param paragraphIndex 0-based index of the target paragraph in the document body
 * @param cleanTarget    the AI/manual clean accepted-state clause
 */
export async function applyViaHybridEngine(
  paragraphIndex: number,
  cleanTarget: string,
  options: HybridApplyOptions = {},
): Promise<HybridApplyResult> {
  let syncCount = 0;

  // A single mutable trace, updated as the pipeline progresses. On any thrown error the catch
  // returns THIS (not a blank), so the log still shows how far we got — baseline length, op counts,
  // protected regions, pre-flight outcome. That is what a "no-op" needs to be diagnosable.
  const trace: HybridApplyTrace = {
    baselineLength: 0,
    targetLength: cleanTarget.length,
    ops: { keep: 0, delete: 0, insert: 0 },
    protectedRegions: { field: 0, bookmark: 0, total: 0 },
    protectedOpCount: 0,
    preflight: { ok: false, total: 0, resolved: 0, failures: [] },
    skipped: 0,
    fieldDeletes: 0,
    unsearchableCodes: [],
    syncCount: 0,
    outcome: 'error',
  };

  try {
    const profile = createQrProfiler('hybrid apply');
    profile.mark('start', { paragraphIndex, targetLen: cleanTarget.length });
    return await Word.run(async (context) => {
      const sync = async (reason?: string) => {
        const t = Date.now();
        syncCount += 1;
        await context.sync();
        const ms = Date.now() - t;
        if (ms >= 150) {
          logQuickResponseStage('profile: hybrid sync', {
            n: syncCount,
            ms,
            reason: reason || '',
          });
        }
      };

      // Resolve the paragraph and read the live visible baseline.
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load('items');
      await sync('load paragraphs.items');

      const rawParagraph = paragraphs.items[paragraphIndex];
      if (!rawParagraph) {
        trace.syncCount = syncCount;
        return { applied: false, reason: 'paragraph_not_found', trace };
      }
      // The office-js typings in this repo infer paragraphs.items as Range[]; the runtime objects are
      // genuinely Word.Paragraph. Cast once here so the seam signatures stay honest.
      const paragraph = rawParagraph as unknown as Word.Paragraph;

      const whole = paragraph.getRange('Whole');
      whole.load('text');
      // Snapshot the pre-apply paragraph OOXML for a DETERMINISTIC undo — restoring this exact
      // package puts the paragraph back including counterparty revisions, no fuzzy tc.reject matching.
      const originalOoxml = whole.getOoxml();
      await sync('paragraph.getOoxml + text');
      profile.mark('baseline + paragraph OOXML', {
        packageLen: (originalOoxml.value || '').length,
        visibleLen: (whole.text || '').length,
      });
      const rawBaseline = whole.text || '';
      const baselineVisible = normalizeWordParagraphBaseline(rawBaseline);
      logQuickResponseStage('hybrid apply: live baseline controls', {
        paragraphIndex,
        raw: controlCharStats(rawBaseline),
        visible: controlCharStats(baselineVisible),
        rawLen: rawBaseline.length,
        visibleLen: baselineVisible.length,
      });
      const foldedBaseline = baselineVisible.replace(/\s+/g, ' ').trim();
      const foldedTarget = cleanTarget.replace(/\s+/g, ' ').trim();
      if (foldedBaseline && foldedBaseline === foldedTarget) {
        logQuickResponseStage('hybrid apply: identity target, nothing to apply', {
          paragraphIndex,
          baselineVisible: qrTextSnapshot('baselineVisible', baselineVisible),
          cleanTarget: qrTextSnapshot('cleanTarget', cleanTarget),
        });
        trace.baselineLength = baselineVisible.length;
        trace.outcome = 'nothing_to_apply';
        trace.syncCount = syncCount;
        return { applied: false, reason: 'nothing_to_apply', trace };
      }
      const applyTarget = foldApplyText(cleanTarget);
      if (!options.skipTruncationGuard && applyTargetLooksTruncated(baselineVisible, applyTarget)) {
        logQuickResponseStage('hybrid apply: refusing truncated target', {
          paragraphIndex,
          baselineVisible: qrTextSnapshot('baselineVisible', baselineVisible),
          cleanTarget: qrTextSnapshot('cleanTarget', cleanTarget),
        });
        trace.baselineLength = baselineVisible.length;
        trace.outcome = 'nothing_to_apply';
        trace.syncCount = syncCount;
        return { applied: false, reason: 'target_truncated', trace };
      }
      const originalParagraphOoxmlPackage = originalOoxml.value || '';
      const inTable =
        OoxmlPackageWrapper.containsTableMarkup(originalParagraphOoxmlPackage) ||
        (await paragraphIsInTable(paragraph, context));
      trace.baselineLength = baselineVisible.length;

      // Protected regions. Only FIELDS block edits (their result is generated content we must not
      // touch). BOOKMARKS are advisory: on this native search/delete path Word preserves bookmark
      // ranges across edits (unlike the old wholesale-OOXML rewrite), so blocking every edit that
      // overlaps one would needlessly veto almost the whole paragraph. We still report bookmark
      // counts for observability.
      const liveHasCommentMark = controlCharStats(rawBaseline).commentRefs > 0;
      const paragraphHasProtectedMarkup =
        liveHasCommentMark ||
        ooxmlParagraphHasProtectedMarkup(originalParagraphOoxmlPackage, baselineVisible);
      let extracted: Awaited<ReturnType<typeof extractProtectedRegions>> = [];
      if (paragraphHasProtectedMarkup) {
        extracted = await extractProtectedRegions(context, paragraph);
        // extractProtectedRegions performs its own sync(s); count one representative flush.
        syncCount += 1;
      }
      profile.mark('protected regions', {
        regionCount: extracted.length,
        skipped: paragraphHasProtectedMarkup ? false : 'no-paragraph-markup',
      });
      trace.protectedRegions = createProtectedRegionSet(extracted.map((e) => e.region)).count();
      logQuickResponseStage('hybrid apply: protected regions', {
        paragraphIndex,
        counts: trace.protectedRegions,
        regions: extracted.map((e) => ({
          kind: e.region.kind,
          start: e.region.start,
          end: e.region.end,
          len: e.region.end - e.region.start,
        })),
        ooxmlMarkup: ooxmlCommentMarkupCounts(originalParagraphOoxmlPackage),
        paragraphHasProtectedMarkup,
        skippedOfficeExtract: !paragraphHasProtectedMarkup,
      });
      try {
        const comments = (
          paragraph.getRange('Whole') as unknown as {
            getComments: () => {
              load: (s: string) => void;
              items?: Array<{ load: (p: string | string[]) => void; content?: string; authorName?: string }>;
            };
          }
        ).getComments();
        comments.load('items');
        await sync('paragraph comments items');
        const commentItems = comments.items || [];
        for (const c of commentItems) c.load(['content', 'authorName']);
        if (commentItems.length) await sync('paragraph comment contents');
        profile.mark('comments', { count: commentItems.length });
        logQuickResponseStage('hybrid apply: paragraph comments', {
          paragraphIndex,
          count: commentItems.length,
          sample: commentItems.slice(0, 5).map((c) => ({
            author: c.authorName || '',
            contentLen: (c.content || '').length,
            contentTail: (c.content || '').slice(-40),
          })),
        });
      } catch (err) {
        logQuickResponseStage('hybrid apply: paragraph comments FAILED', {
          paragraphIndex,
          message: err instanceof Error ? err.message : String(err),
        });
      }

      // Field regions sorted the SAME way createProtectedRegionSet sorts, so a fieldDelete op's
      // fieldRegionIndex (an index into protectedSet.regions) aligns with fieldRanges below.
      const fieldEntries = extracted
        .filter((e) => e.region.kind === 'field')
        .sort((a, b) => a.region.start - b.region.start || a.region.end - b.region.end);
      const commentMarks = [...rawBaseline].reduce<number[]>((acc, ch, i) => {
        if (ch === '\u0005') acc.push(i);
        return acc;
      }, []);
      const mapOff = (off: number): number => {
        let delta = 0;
        for (const m of commentMarks) if (m < off) delta += 1;
        return Math.max(0, off - delta);
      };
      const blocking = extracted
        .filter((e) => e.region.kind === 'field' || e.region.kind === 'comment')
        .map((e) => ({
          ...e.region,
          start: mapOff(e.region.start),
          end: Math.min(mapOff(e.region.end), baselineVisible.length),
        }))
        .filter((r) => r.end > r.start && r.start < baselineVisible.length);
      const protectedSet = createProtectedRegionSet(blocking);
      const fieldRanges = fieldEntries.map((e) => e.range);
      // Keep the field range proxies alive across syncs into the apply pass.
      for (const fr of fieldRanges) {
        if (fr) (context.trackedObjects.add as (o: Word.Range) => void)(fr);
      }

      // Existing tracked revisions — so the engine preserves counterparty edits instead of re-diffing
      // them into new our-side edits. Caller override wins (for tests); otherwise read from the doc.
      const revisionContext =
        options.revisionContext ??
        (await extractRevisionsForNormalizedBaseline(
          context,
          paragraph,
          rawBaseline,
          baselineVisible,
          originalParagraphOoxmlPackage,
        ));
      syncCount += 1;
      profile.mark('extract revisions', {
        revisionCount: revisionContext.revisions.length,
      });
      logQuickResponseStage('hybrid apply: revision context', {
        paragraphIndex,
        revisionCount: revisionContext.revisions.length,
        deletions: revisionContext.revisions.filter((r) => r.kind === 'deletion').length,
        insertions: revisionContext.revisions.filter((r) => r.kind === 'insertion').length,
        sample: revisionContext.revisions.slice(0, 8).map((r) => ({
          kind: r.kind,
          start: r.start,
          end: r.end,
          len: r.end - r.start,
          side: r.side,
        })),
      });

      // Pure plan.
      const plan = buildApplyPlan(baselineVisible, applyTarget, protectedSet, revisionContext);
      debugHybridPipeline('apply', baselineVisible, applyTarget, revisionContext, plan.targetVisible, plan.ops);
      const insertOps = plan.ops.filter((op) => op.kind === 'insert');
      const deleteOps = plan.ops.filter((op) => op.kind === 'delete');
      logQuickResponseStage('hybrid apply: plan built', {
        paragraphIndex,
        baselineVisible: qrTextSnapshot('baselineVisible', baselineVisible),
        cleanTarget: qrTextSnapshot('cleanTarget', cleanTarget),
        targetVisible: qrTextSnapshot('targetVisible', plan.targetVisible),
        opHistogram: plan.histogram,
        insertOpCount: insertOps.length,
        insertOps: insertOps.map((op) => qrTextSnapshot('insertLogical', op.value)),
        deleteOpCount: deleteOps.length,
        deleteOps: deleteOps.map((op) => qrTextSnapshot('deleteLogical', op.value)),
        deleteTotalLen: deleteOps.reduce((n, op) => n + op.value.length, 0),
        locatedInsertCount: plan.located.filter((l) => l.kind === 'insert').length,
        locatedDeleteCount: plan.located.filter((l) => l.kind === 'delete').length,
      });
      profile.mark('plan built', {
        keep: plan.histogram.keep,
        delete: plan.histogram.delete,
        insert: plan.histogram.insert,
      });
      for (const op of insertOps) {
        if (/[\[\](){}]/.test(op.value)) {
          logQuickResponseBidiCompare('hybrid apply plan insert (logical)', op.value);
        }
      }
      trace.targetLength = plan.targetVisible.length;
      trace.ops = plan.histogram;
      trace.fieldDeletes = plan.located.filter((l) => l.kind === 'fieldDelete').length;
      // Diagnostics: which char codes (if any) make an op's search value unsearchable.
      const codes = new Set<number>();
      for (const l of plan.located) {
        const value = l.kind === 'delete' ? l.value : l.kind === 'insert' && l.anchor.type !== 'start' && l.anchor.type !== 'end' ? l.anchor.value : '';
        for (const c of unsearchableCharCodesOf(value)) codes.add(c);
      }
      trace.unsearchableCodes = [...codes].sort((a, b) => a - b);
      trace.protectedOpCount = plan.protectedOpCount;
      trace.preflight = { ok: false, total: plan.located.length, resolved: 0, failures: [] };

      // Pre-flight: resolve everything read-only, or abort.
      const { plan: preflightPlan, ranges } = await preflightLocate(context, paragraph, plan.located);
      syncCount += 1; // preflightLocate's single batched search sync
      profile.mark('preflight locate', {
        ok: preflightPlan.ok,
        total: preflightPlan.total,
        resolved: preflightPlan.resolvedCount,
      });
      trace.preflight = {
        ok: preflightPlan.ok,
        total: preflightPlan.total,
        resolved: preflightPlan.resolvedCount,
        failures: preflightPlan.failures.map((f) => ({ kind: f.kind, reason: f.reason })),
      };
      trace.skipped = preflightPlan.failures.length;

      // Incomplete pre-flight → do not apply a subset (insert-without-delete corrupts)
      // and do not replace the whole paragraph (that paints the clause as one del+ins).
      if (!preflightPlan.ok || ranges.length === 0) {
        for (const resolved of ranges) {
          if (resolved.range) {
            (context.trackedObjects.remove as (o: Word.Range) => void)(resolved.range);
          }
        }
        trace.outcome = 'nothing_to_apply';
        trace.syncCount = syncCount;
        return { applied: false, reason: 'nothing_to_apply', trace };
      }

      // Snapshot existing tracked-change fingerprints so undo can tell OUR new changes from the
      // counterparty's (author-free — Office.js stamps native edits with the live user).
      const beforeFingerprints = await collectFingerprints(paragraph, context);
      syncCount += 2; // collectFingerprints' two loads
      profile.mark('fingerprints before');
      const applyStartedAtMs = Date.now();

      // Force track changes on, remembering the prior mode to restore it.
      const doc = context.document;
      doc.load('changeTrackingMode');
      await sync('changeTrackingMode');
      const priorMode = doc.changeTrackingMode;
      doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      // Execute right-to-left (ranges are already ordered by descending offset). Queue all
      // mutations, then flush once.
      for (const resolved of ranges) {
        if (resolved.range) resolved.range.load('text');
      }
      await sync('preflight range texts');
      const oversizedDelete = ranges.find((resolved) => {
        if (resolved.op.kind !== 'delete' || !resolved.range) return false;
        const rangeLen = (resolved.range.text || '').length;
        const valueLen = resolved.op.value.length;
        return rangeLen > Math.max(valueLen + 8, valueLen * 2);
      });
      if (oversizedDelete && oversizedDelete.op.kind === 'delete') {
        logQuickResponseStage('hybrid apply: refuse oversized delete range', {
          paragraphIndex,
          deleteValue: qrTextSnapshot('deleteValue', oversizedDelete.op.value),
          rangeText: qrTextSnapshot('rangeText', oversizedDelete.range?.text || ''),
        });
        for (const resolved of ranges) {
          if (resolved.range) {
            (context.trackedObjects.remove as (o: Word.Range) => void)(resolved.range);
          }
        }
        for (const fr of fieldRanges) {
          if (fr) (context.trackedObjects.remove as (o: Word.Range) => void)(fr);
        }
        doc.changeTrackingMode = priorMode;
        trace.outcome = 'error';
        trace.syncCount = syncCount;
        return { applied: false, reason: 'oversized_delete_range', trace };
      }
      for (const resolved of ranges) {
        const op = resolved.op;
        if (op.kind === 'delete') {
          logQuickResponseStage('hybrid apply: executing delete', {
            paragraphIndex,
            deleteValue: qrTextSnapshot('deleteValue', op.value),
            rangeText: qrTextSnapshot('rangeText', resolved.range?.text || ''),
            rangeMatchesValue: (resolved.range?.text || '') === op.value,
          });
          if (resolved.range) resolved.range.delete();
        } else if (op.kind === 'fieldDelete') {
          // Delete the whole field via its own range (a redline fully spans it).
          const fr = fieldRanges[op.fieldRegionIndex];
          if (fr) fr.delete();
        } else {
          // insert — placement depends on the anchor kind
          logQuickResponseStage('hybrid apply: executing insert', {
            paragraphIndex,
            anchorType: op.anchor.type,
            insertValue: qrTextSnapshot('insertValue', op.value),
          });
          if (/[\[\](){}]/.test(op.value)) {
            logQuickResponseBidiCompare('hybrid apply insertText arg', op.value);
          }
          if (op.anchor.type === 'start') {
            paragraph.insertText(op.value, Word.InsertLocation.start);
          } else if (op.anchor.type === 'end') {
            paragraph.insertText(op.value, Word.InsertLocation.end);
          } else if (resolved.range) {
            const where =
              op.anchor.type === 'after' ? Word.InsertLocation.after : Word.InsertLocation.before;
            resolved.range.insertText(op.value, where);
          }
        }
      }
      await sync('execute mutations');
      profile.mark('execute mutations', { opCount: ranges.length });

      const liveAfterOps = await readParagraphForVerify(paragraph, sync);
      // Plan-based verify (Mac Word's paragraph.getTrackedChanges() returns 0
      // items even with visible redline; getOoxml() returns the fat package with
      // sibling <w:p>s so accepted extraction can pick the wrong one). With track
      // changes on, the deletes stay visible as strikethrough → post-apply visible
      // length must equal baseline length + total insert length, and every insert
      // value must be present in visible. If either accepted extract matches the
      // target that also counts as verified.
      const executedInserts = ranges.filter((r) => r.op.kind === 'insert');
      const insertsTotalLen = executedInserts.reduce((n, r) => n + (r.op.kind === 'insert' ? r.op.value.length : 0), 0);
      const expectedVisibleLen = baselineVisible.length + insertsTotalLen;
      const visibleFold = foldApplyText(liveAfterOps.visible);
      const expectedFoldLen = foldApplyText(baselineVisible).length + insertsTotalLen;
      const lengthOk =
        Math.abs(liveAfterOps.visible.length - expectedVisibleLen) <= 1 ||
        Math.abs(visibleFold.length - expectedFoldLen) <= 1;
      const insertsPresent = executedInserts.every((r) => {
        if (r.op.kind !== 'insert') return true;
        const needle = foldApplyText(r.op.value);
        return needle.length === 0 || visibleFold.includes(needle);
      });
      const acceptedMatches = foldedApplyMatchesTarget(liveAfterOps.accepted, applyTarget);
      const visibleMatches = foldedApplyMatchesTarget(liveAfterOps.visible, applyTarget);
      const verifyOk = acceptedMatches || visibleMatches || (lengthOk && insertsPresent);
      logQuickResponseStage('hybrid apply: post-op verify decision', {
        paragraphIndex,
        verifyOk,
        acceptedMatches,
        visibleMatches,
        lengthOk,
        insertsPresent,
        expectedVisibleLen,
        actualVisibleLen: liveAfterOps.visible.length,
        acceptedLen: liveAfterOps.accepted.length,
        insertsTotalLen,
        insertCount: executedInserts.length,
      });
      profile.mark('post-op verify', {
        verifyOk,
        visibleLen: liveAfterOps.visible.length,
        acceptedLen: liveAfterOps.accepted.length,
      });
      if (!verifyOk) {
        logQuickResponseStage('hybrid apply: post-op verify mismatch', {
          paragraphIndex,
          liveAfterOps: qrTextSnapshot('liveAfterOps', liveAfterOps.visible),
          liveAfterOpsAccepted: qrTextSnapshot('liveAfterOpsAccepted', liveAfterOps.accepted),
          cleanTarget: qrTextSnapshot('cleanTarget', applyTarget),
        });
        doc.changeTrackingMode = Word.ChangeTrackingMode.off;
        await restoreParagraphOoxml(
          paragraph,
          originalParagraphOoxmlPackage,
          baselineVisible,
          inTable,
          sync,
        );
        for (const resolved of ranges) {
          if (resolved.range) {
            (context.trackedObjects.remove as (o: Word.Range) => void)(resolved.range);
          }
        }
        for (const fr of fieldRanges) {
          if (fr) (context.trackedObjects.remove as (o: Word.Range) => void)(fr);
        }
        doc.changeTrackingMode = priorMode;
        trace.outcome = 'error';
        trace.syncCount = syncCount;
        return { applied: false, reason: 'verify_mismatch', trace };
      }

      // Restore the caller's prior tracking mode and release tracked proxies.
      doc.changeTrackingMode = priorMode;
      for (const resolved of ranges) {
        if (resolved.range) {
          (context.trackedObjects.remove as (o: Word.Range) => void)(resolved.range);
        }
      }
      for (const fr of fieldRanges) {
        if (fr) (context.trackedObjects.remove as (o: Word.Range) => void)(fr);
      }
      await sync('release tracked ranges');
      profile.mark('cleanup tracking mode');

      // Capture the fingerprints our edits added, plus the apply window, for undo.
      const afterFingerprints = await collectFingerprints(paragraph, context);
      syncCount += 2;
      profile.mark('fingerprints after + revert package', { syncCount });
      const revertContext = buildHybridRevertContext({
        paragraphIndex,
        applyStartedAtMs,
        applyFinishedAtMs: Date.now(),
        addedFingerprints: addedFingerprints(beforeFingerprints, afterFingerprints),
        baselineVisible,
        rawOoxmlPackage: originalParagraphOoxmlPackage,
        inTable,
      });

      trace.outcome = 'applied';
      trace.syncCount = syncCount;
      return {
        applied: true,
        trace,
        revertContext,
      };
    });
  } catch (err) {
    // Preserve whatever the trace captured before the throw; only stamp outcome + sync count.
    trace.outcome = 'error';
    trace.syncCount = syncCount;
    return {
      applied: false,
      reason: err instanceof Error ? err.message : String(err),
      trace,
    };
  }
}
